import {defineStore} from 'pinia'
import baseURLs from '@/api/APIBaseURLs'
import {Config, ToolkitsActive} from '@/models/Config'
import {Optional} from '@/models/TypeHelpers'
import {FESettingsInstance} from '@/auth/FESettings'
import {isAlwaysAccessiblePage, isSuperuser} from '@/auth/ToolkitHelpers'

export const useGeneralStore = defineStore('general', {
  state: () => ({
    currentModule: null as Optional<string>,
    currentStoresSetsLabel: 'buy' as Optional<string>,
    appConfig: null as Optional<Config>,
    accessDeniedDialogVisible: false,
    accessDeniedDialogContent: '',
    devMode: localStorage.getItem('devMode') === 'true',
    orgName: localStorage.getItem('org'),
    snackOpened: false,
    snackText: '',
    snackType: '',
    showLoadingLayout: true,
  }),
  actions: {
    setDevMode (isDev = true) {
      if (this.devMode === isDev) {
        return
      }
      this.devMode = isDev
      localStorage.setItem('devMode', String(isDev))
      baseURLs.setDevMode(isDev)
    },
    setSnack (type: 'warning'| 'success' | 'error', text = '' ){
      this.snackOpened = true
      this.snackText = text
      this.snackType = type
    },
    setAppConfig (payload: Config) {
      this.appConfig = payload
      const org_key = Number(localStorage.getItem('org_key') || 0)
      if (payload?.fe_settings) {
        FESettingsInstance.setSettingsFromConfig(payload.fe_settings, org_key || 0)
      }
    },
    setCurrentModule (payload: string) {
      if(this.appConfig?.stores_sets_labels?.includes(payload)) {
        this.setCurrentStoresSetsLabel(payload)
      }
      this.currentModule = payload
    },
    setCurrentStoresSetsLabel (payload: Optional<string>){
      this.currentStoresSetsLabel = payload
    },
    setAccessDeniedDialogVisible (payload: boolean) {
      this.accessDeniedDialogVisible = payload
      if(!payload) {
        this.accessDeniedDialogContent = ''
      }
    },
    setAccessDeniedDialogContent (payload: string) {
      this.accessDeniedDialogContent = payload
    },
    setShowLoadingLayout (payload: boolean) {
      this.showLoadingLayout = payload
    },
  },
  getters: {
    buySettings: (state) => {
      if(!state.appConfig?.org_settings){
        return null
      }
      return state.appConfig?.org_settings[0]?.buy_settings || null
    },
    /**
     * Returns the toolkits_active configuration from org_settings
     */
    toolkitsActive: (state): ToolkitsActive|null => {
      return state.appConfig?.org_settings?.[0]?.toolkits_active || null
    },
    /**
     * Returns a function that checks if a module is accessible
     */
    canAccessModule: (state) => (module: string, ignoreSuperuser = false): boolean => {
      if (isSuperuser() && !ignoreSuperuser) {
        return true
      }

      const toolkits = state.appConfig?.org_settings?.[0]?.toolkits_active || {}
      return module in toolkits
    },
    /**
     * Returns a function that checks if a page is accessible within a module
     */
    canAccessPage: (state) => (module: string, page: string, ignoreSuperuser = false): boolean => {
      if (isSuperuser() && !ignoreSuperuser) {
        return true
      }

      // Always accessible pages
      if (isAlwaysAccessiblePage(module, page)) {
        return true
      }

      const toolkits = state.appConfig?.org_settings?.[0]?.toolkits_active || {}
      const modulePages = toolkits[module] || []
      return modulePages.includes(page)
    },
    /**
     * Returns a function that checks if a module should be shown as locked in dropdown
     */
    isModuleLocked: (state) => (module: string, ignoreSuperuser = false): boolean => {
      if (isSuperuser() && !ignoreSuperuser) {
        return false
      }

      const toolkits = state.appConfig?.org_settings?.[0]?.toolkits_active || {}
      return module in toolkits && toolkits[module].length === 0
    },
    /**
     * this returns a list of stores with label and value properties
     */
    mappedConfigStores: (state) => {
      return (state.appConfig?.stores || []).map(s => ({ label: s.store_name, value: s.store_key }))
    },

    /**
     * Returns a function that takes a store key and returns the store object
     */
    findStoreWithKey: (state) => (storeKey: number) => {
      storeKey = Number(storeKey)
      return (state.appConfig?.stores || []).find(s => Number(s.store_key) === storeKey)
    }
  }
})
